import React, { useEffect, useMemo, useState } from 'react';
import { HotelPricingRange, HotelRatePlan, HotelRatePlanUpsertPayload } from '../../types/HotelAdmin';
import '../../css/modal.css';

interface RatePlanFormModalProps {
  isOpen: boolean;
  initialRatePlan?: HotelRatePlan | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: HotelRatePlanUpsertPayload) => Promise<void> | void;
}

type PricingRangeFormState = {
  start_date: string;
  end_date: string;
  single_occupancy_price: string;
  double_occupancy_price: string;
  extra_adult_price: string;
  child_price: string;
  tax_amount: string;
  currency: string;
};

type RatePlanFormState = {
  plan_code: string;
  room_name: string;
  meal_type: string;
  max_adults: string;
  max_children: string;
  pricing_ranges: PricingRangeFormState[];
  refundable: boolean;
  free_cancel_until_hours: string;
  post_cutoff_charge_type: string;
  post_cutoff_charge_value: string;
  confirmation_mode: string;
  is_active: boolean;
};

const DEFAULT_RATE_PLAN_VALUES = {
  max_adults: '2',
  max_children: '1',
  free_cancel_until_hours: '48',
  post_cutoff_charge_type: 'FIXED',
  post_cutoff_charge_value: '100',
  confirmation_mode: 'MANUAL',
  tax_amount: '0',
  currency: 'INR',
} as const;

const toText = (value: unknown) => (value === null || value === undefined ? '' : String(value));

const toDateText = (value: unknown) => {
  const text = toText(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
};

const hyphenate = (value: string) =>
  value
    .trim()
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');

const buildDefaultPlanCode = (roomName: string, mealType: string) => {
  const parts = [hyphenate(roomName), hyphenate(mealType)].filter(Boolean);
  return parts.join('-');
};

const toRangeState = (range?: Partial<HotelPricingRange> | null, fallback?: Partial<HotelRatePlan>): PricingRangeFormState => ({
  start_date: toDateText(range?.start_date),
  end_date: toDateText(range?.end_date),
  single_occupancy_price: toText(range?.single_occupancy_price ?? fallback?.price ?? 0),
  double_occupancy_price: toText(range?.double_occupancy_price ?? fallback?.price ?? 0),
  extra_adult_price: toText(range?.extra_adult_price ?? 0),
  child_price: toText(range?.child_price ?? 0),
  tax_amount: toText(range?.tax_amount ?? fallback?.tax_amount ?? DEFAULT_RATE_PLAN_VALUES.tax_amount),
  currency: toText(range?.currency || fallback?.currency || DEFAULT_RATE_PLAN_VALUES.currency),
});

const toInitialState = (ratePlan?: HotelRatePlan | null): RatePlanFormState => {
  const ranges = Array.isArray(ratePlan?.pricing_ranges) && ratePlan!.pricing_ranges!.length
    ? ratePlan!.pricing_ranges!.map((range) => toRangeState(range, ratePlan || undefined))
    : [toRangeState(null, ratePlan || undefined)];
  const roomName = toText(ratePlan?.room_name);
  const mealType = toText(ratePlan?.meal_type);
  const isEditing = Boolean(ratePlan);

  return {
    plan_code: toText(ratePlan?.plan_code || buildDefaultPlanCode(roomName, mealType)),
    room_name: roomName,
    meal_type: mealType,
    max_adults: toText(ratePlan?.max_adults ?? (isEditing ? '' : DEFAULT_RATE_PLAN_VALUES.max_adults)),
    max_children: toText(ratePlan?.max_children ?? (isEditing ? '' : DEFAULT_RATE_PLAN_VALUES.max_children)),
    pricing_ranges: ranges,
    refundable: typeof ratePlan?.refundable === 'boolean' ? ratePlan.refundable : true,
    free_cancel_until_hours: toText(ratePlan?.free_cancel_until_hours ?? (isEditing ? '' : DEFAULT_RATE_PLAN_VALUES.free_cancel_until_hours)),
    post_cutoff_charge_type: toText(ratePlan?.post_cutoff_charge_type || DEFAULT_RATE_PLAN_VALUES.post_cutoff_charge_type),
    post_cutoff_charge_value: toText(ratePlan?.post_cutoff_charge_value ?? (isEditing ? '' : DEFAULT_RATE_PLAN_VALUES.post_cutoff_charge_value)),
    confirmation_mode: toText(ratePlan?.confirmation_mode || DEFAULT_RATE_PLAN_VALUES.confirmation_mode),
    is_active: typeof ratePlan?.is_active === 'boolean' ? ratePlan.is_active : true,
  };
};

const parseMoney = (value: string, label: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${label} must be a valid number greater than or equal to 0.`);
  }
  return parsed;
};

const RatePlanFormModal: React.FC<RatePlanFormModalProps> = ({
  isOpen,
  initialRatePlan = null,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<RatePlanFormState>(() => toInitialState(initialRatePlan));
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setFormData(toInitialState(initialRatePlan));
    setFormError('');
  }, [isOpen, initialRatePlan]);

  const title = useMemo(
    () => (initialRatePlan ? 'Edit Room Variant' : 'Add Room Variant'),
    [initialRatePlan]
  );

  if (!isOpen) return null;

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (!initialRatePlan && (name === 'room_name' || name === 'meal_type')) {
        next.plan_code = buildDefaultPlanCode(
          name === 'room_name' ? value : next.room_name,
          name === 'meal_type' ? value : next.meal_type
        );
      }
      return next;
    });
  };

  const updatePricingRange = (index: number, key: keyof PricingRangeFormState, value: string) => {
    setFormData((prev) => ({
      ...prev,
      pricing_ranges: prev.pricing_ranges.map((range, idx) =>
        idx === index ? { ...range, [key]: value } : range
      ),
    }));
  };

  const addPricingRange = () => {
    setFormData((prev) => ({
      ...prev,
      pricing_ranges: [...prev.pricing_ranges, toRangeState(null, { currency: prev.pricing_ranges[0]?.currency || DEFAULT_RATE_PLAN_VALUES.currency })],
    }));
  };

  const removePricingRange = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      pricing_ranges: prev.pricing_ranges.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError('');

    try {
      const requiredFields: Array<[string, string]> = [
        ['plan_code', formData.plan_code],
        ['room_name', formData.room_name],
        ['meal_type', formData.meal_type],
        ['max_adults', formData.max_adults],
        ['max_children', formData.max_children],
        ['free_cancel_until_hours', formData.free_cancel_until_hours],
        ['post_cutoff_charge_type', formData.post_cutoff_charge_type],
        ['post_cutoff_charge_value', formData.post_cutoff_charge_value],
        ['confirmation_mode', formData.confirmation_mode],
      ];

      const missingField = requiredFields.find(([, value]) => value.trim() === '');
      if (missingField) {
        setFormError(`Please provide ${missingField[0]}.`);
        return;
      }

      const isEditing = Boolean(initialRatePlan);
      const maxAdults = isEditing ? Number(formData.max_adults) : Number(DEFAULT_RATE_PLAN_VALUES.max_adults);
      const maxChildren = isEditing ? Number(formData.max_children) : Number(DEFAULT_RATE_PLAN_VALUES.max_children);
      const freeCancelHours = isEditing ? Number(formData.free_cancel_until_hours) : Number(DEFAULT_RATE_PLAN_VALUES.free_cancel_until_hours);
      const postCutoffChargeValue = isEditing ? Number(formData.post_cutoff_charge_value) : Number(DEFAULT_RATE_PLAN_VALUES.post_cutoff_charge_value);
      const postCutoffChargeType = isEditing ? formData.post_cutoff_charge_type.trim().toUpperCase() : DEFAULT_RATE_PLAN_VALUES.post_cutoff_charge_type;
      const confirmationMode = isEditing ? formData.confirmation_mode.trim().toUpperCase() : DEFAULT_RATE_PLAN_VALUES.confirmation_mode;
      const planCode = isEditing ? formData.plan_code.trim() : buildDefaultPlanCode(formData.room_name, formData.meal_type);
      const currency = isEditing ? (formData.pricing_ranges[0]?.currency.trim().toUpperCase() || DEFAULT_RATE_PLAN_VALUES.currency) : DEFAULT_RATE_PLAN_VALUES.currency;

      if (isEditing && (!Number.isInteger(maxAdults) || maxAdults <= 0)) {
        setFormError('Max adults must be an integer greater than 0.');
        return;
      }
      if (isEditing && (!Number.isInteger(maxChildren) || maxChildren < 0)) {
        setFormError('Max children must be an integer greater than or equal to 0.');
        return;
      }
      if (isEditing && (!Number.isFinite(freeCancelHours) || freeCancelHours < 0)) {
        setFormError('Free cancel until hours must be greater than or equal to 0.');
        return;
      }
      if (isEditing && (!Number.isFinite(postCutoffChargeValue) || postCutoffChargeValue < 0)) {
        setFormError('Post-cutoff charge value must be greater than or equal to 0.');
        return;
      }
      if (formData.pricing_ranges.length === 0) {
        setFormError('Please add at least one pricing range.');
        return;
      }

      const pricingRanges: HotelPricingRange[] = formData.pricing_ranges.map((range, index) => {
        if (range.start_date && range.end_date && range.start_date > range.end_date) {
          throw new Error(`Pricing range ${index + 1} start date must be before end date.`);
        }
        const rangeCurrency = isEditing ? range.currency.trim().toUpperCase() : DEFAULT_RATE_PLAN_VALUES.currency;
        if (isEditing && !rangeCurrency) {
          throw new Error(`Pricing range ${index + 1} currency is required.`);
        }
        return {
          start_date: range.start_date || null,
          end_date: range.end_date || null,
          single_occupancy_price: parseMoney(range.single_occupancy_price, `Pricing range ${index + 1} single occupancy price`),
          double_occupancy_price: parseMoney(range.double_occupancy_price, `Pricing range ${index + 1} double occupancy price`),
          extra_adult_price: parseMoney(range.extra_adult_price, `Pricing range ${index + 1} extra adult price`),
          child_price: parseMoney(range.child_price, `Pricing range ${index + 1} child price`),
          tax_amount: isEditing
            ? parseMoney(range.tax_amount, `Pricing range ${index + 1} tax amount`)
            : Number(DEFAULT_RATE_PLAN_VALUES.tax_amount),
          currency: rangeCurrency || DEFAULT_RATE_PLAN_VALUES.currency,
        };
      });

      const payload: HotelRatePlanUpsertPayload = {
        plan_code: planCode,
        room_name: formData.room_name.trim(),
        meal_type: formData.meal_type.trim(),
        max_adults: maxAdults,
        max_children: maxChildren,
        currency,
        pricing_ranges: pricingRanges,
        refundable: formData.refundable,
        free_cancel_until_hours: freeCancelHours,
        post_cutoff_charge_type: postCutoffChargeType,
        post_cutoff_charge_value: postCutoffChargeValue,
        confirmation_mode: confirmationMode,
        is_active: formData.is_active,
      };

      await onSubmit(payload);
    } catch (error: any) {
      setFormError(error?.message || 'Invalid pricing details.');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg hotels-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            x
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="hotels-form-grid">
            <label>
              Room Type *
              <input name="room_name" value={formData.room_name} onChange={handleInputChange} />
            </label>
            <label>
              Meal Type *
              <input name="meal_type" value={formData.meal_type} onChange={handleInputChange} />
            </label>
          </div>

          <div className="hotels-pricing-ranges">
            <div className="hotels-pricing-ranges-header">
              <h3>Seasonal Pricing</h3>
              <button type="button" className="btn-secondary" onClick={addPricingRange}>
                Add Date Range
              </button>
            </div>
            {formData.pricing_ranges.map((range, index) => (
              <div className="hotels-pricing-range" key={`pricing-range-${index}`}>
                <div className="hotels-pricing-range-title">
                  <span>Date Range {index + 1}</span>
                  {formData.pricing_ranges.length > 1 && (
                    <button type="button" className="file-chip-remove" onClick={() => removePricingRange(index)}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="hotels-form-grid">
                  <label>
                    Start Date
                    <input
                      type="date"
                      value={range.start_date}
                      onChange={(event) => updatePricingRange(index, 'start_date', event.target.value)}
                    />
                  </label>
                  <label>
                    End Date
                    <input
                      type="date"
                      value={range.end_date}
                      onChange={(event) => updatePricingRange(index, 'end_date', event.target.value)}
                    />
                  </label>
                  <label>
                    Single Occupancy Price *
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={range.single_occupancy_price}
                      onChange={(event) => updatePricingRange(index, 'single_occupancy_price', event.target.value)}
                    />
                  </label>
                  <label>
                    Double Occupancy Price *
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={range.double_occupancy_price}
                      onChange={(event) => updatePricingRange(index, 'double_occupancy_price', event.target.value)}
                    />
                  </label>
                  <label>
                    Extra Adult Price
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={range.extra_adult_price}
                      onChange={(event) => updatePricingRange(index, 'extra_adult_price', event.target.value)}
                    />
                  </label>
                  <label>
                    Child Price
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={range.child_price}
                      onChange={(event) => updatePricingRange(index, 'child_price', event.target.value)}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="hotels-form-checkboxes">
            <label className="hotels-checkbox">
              <input
                type="checkbox"
                name="refundable"
                checked={formData.refundable}
                onChange={handleInputChange}
              />
              <span>Refundable</span>
            </label>
            <label className="hotels-checkbox">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span>Is Active</span>
            </label>
          </div>

          {formError && <div className="hotels-form-error">{formError}</div>}

          <div className="modal-actions">
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RatePlanFormModal;
